import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { CARD_ICON } from '../icons';
import {
	installDefaultLibraryIconLoader,
	installDefaultLibraryIconPackLoader,
	isLibraryIconName,
	libraryIconSnapshot,
	requestLibraryIcon,
	subscribeLibraryIcon,
	warnIconOnce,
} from '../../utils/libraryIcons';

/**
 * LibraryIcon — draws one icon of the library by its stored name (KPR-1187,
 * KPE-037 "icons on demand"). Every surface that draws an icon a person chose
 * (a record badge's tile, a user's avatar, a choice's chip or option, the icon
 * picker, Admin › Icons) draws it through here:
 *
 *   <LibraryIcon name="DOG" />
 *   <LibraryIcon name={record.icon} fallback={CARD_ICON} className="…" size={16} />
 *
 * WHY. The library is ~2,200 drawings. Looked up as `Icons[name]` on an
 * `import * as` of components/icons.js they all shipped in main.js (~560 KB
 * gzip) on every page. Now the build writes one file per icon
 * (scripts/build-icon-files.mjs: `public/icons/<NAME>.<hash>.json`, cached for
 * a year by firebase.json) and this component fetches the one it draws. The
 * app's own chrome glyphs (a chevron, the trash can) are still named imports
 * from components/icons.js and stay in the bundle.
 *
 * HOW IT DRAWS.
 * - A name the library does not have (an old guessed `home`, a prototype key
 *   such as `constructor`) is never fetched: it draws `fallback` (the generic
 *   card glyph unless the caller says otherwise; `null` draws nothing) and is
 *   said once on the console. No name at all draws `fallback` quietly.
 * - While the file is on its way it draws an EMPTY <svg> of the same size
 *   (1em, or `size`), so a tile, a chip or a button never changes size when
 *   the glyph arrives.
 * - Once it has the file it draws the <svg> exactly as react-icons' IconBase
 *   does (the same attributes; `size`, `color`, `title`, `className`, `style`
 *   and any other prop passed through), so a surface that drew a react-icons
 *   component before looks the same.
 * - A file that fails (offline, a non-200, a body that is not an icon file:
 *   after a deploy an old tab's hash is gone and the host answers with the app
 *   shell) draws `fallback`, said once (utils/libraryIcons.js holds the cache
 *   and the retry).
 *
 * `deferred` waits until the icon scrolls near the viewport before it asks for
 * its file (Admin › Icons draws the whole library on one page). Without
 * IntersectionObserver it asks at once.
 *
 * `managed` is for a surface that fetches artwork itself, a page at a time,
 * and only wants this icon drawn when it arrives (the icon picker asks for a
 * category's PACK, KPR-1188): the icon draws what the cache holds, never asks
 * for its own file, and shows the same-size empty glyph until the surface's
 * request lands. A name that is not an icon still draws the fallback.
 */

async function fetchArtwork(name, url) {
	if (!url) throw new Error(`"${name}" has no file`);
	if (typeof fetch !== 'function') throw new Error('no fetch here');
	const response = await fetch(url);
	if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
	return response.json();
}

installDefaultLibraryIconLoader(fetchArtwork);

// A category pack (config/iconPacks.js) for the icon picker: the same fetch,
// the same checks (utils/libraryIcons.js validates the body and every member).
async function fetchPack(id, url) {
	if (!url) throw new Error(`pack "${id}" has no file`);
	if (typeof fetch !== 'function') throw new Error('no fetch here');
	const response = await fetch(url);
	if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
	return response.json();
}

installDefaultLibraryIconPackLoader(fetchPack);

const SVG_NS = 'http://www.w3.org/2000/svg';
const PLACEHOLDER_ATTR = Object.freeze({ viewBox: '0 0 24 24' });

function drawTree(nodes) {
	if (!Array.isArray(nodes)) return null;
	return nodes.map((node, index) => React.createElement(node.tag, { key: index, ...node.attr }, drawTree(node.child)));
}

/** IconBase's own <svg> (react-icons lib/iconBase.js) around the artwork's tree — or nothing yet, while it loads. */
function drawSvg(artwork, props, extra) {
	const { size, title, color, ...svgProps } = props;
	const computed = size || '1em';
	return React.createElement(
		'svg',
		{
			stroke: 'currentColor',
			fill: 'currentColor',
			strokeWidth: '0',
			...(artwork ? artwork.attr : PLACEHOLDER_ATTR),
			...svgProps,
			...extra,
			className: props.className,
			style: { color, ...props.style },
			height: computed,
			width: computed,
			xmlns: SVG_NS,
		},
		title ? React.createElement('title', null, title) : null,
		artwork ? drawTree(artwork.child) : null,
	);
}

/** False until the node has come within 200px of the viewport, when `enabled`; true at once otherwise. */
function useNearViewport(ref, enabled) {
	const [near, setNear] = useState(!enabled);
	useEffect(() => {
		if (near) return undefined;
		const node = ref.current;
		if (!node || typeof IntersectionObserver === 'undefined') {
			setNear(true);
			return undefined;
		}
		const observer = new IntersectionObserver((records) => {
			if (records.some(record => record.isIntersecting)) {
				setNear(true);
				observer.disconnect();
			}
		}, { rootMargin: '200px' });
		observer.observe(node);
		return () => observer.disconnect();
	}, [ref, near]);
	return near;
}

export default function LibraryIcon({ name, fallback = CARD_ICON, deferred = false, managed = false, ...props }) {
	const placeholderRef = useRef(null);
	const known = isLibraryIconName(name);
	const subscribe = useCallback(listener => subscribeLibraryIcon(name, listener), [name]);
	const read = useCallback(() => libraryIconSnapshot(name), [name]);
	const snap = useSyncExternalStore(subscribe, read, read);
	const near = useNearViewport(placeholderRef, deferred && known);

	useEffect(() => {
		if (known) {
			if (near && !managed) requestLibraryIcon(name);
		} else if (name !== undefined && name !== null && name !== '') {
			warnIconOnce(name, `"${name}" is not an icon of the library`);
		}
	}, [name, known, near, managed]);

	useEffect(() => {
		if (snap.state === 'failed') warnIconOnce(name, `the icon "${name}" could not be loaded (${snap.reason || 'no reason given'})`);
	}, [name, snap]);

	if (snap.state === 'ready') return drawSvg(snap.artwork, props);
	if (snap.state === 'unknown' || snap.state === 'failed') {
		const Fallback = fallback;
		return Fallback ? <Fallback {...props} /> : null;
	}
	return drawSvg(null, props, { ref: placeholderRef, 'data-icon-loading': '' });
}

const COMPONENTS = new Map();

/**
 * A component that draws one library icon — `(props) => <LibraryIcon name={name} {...props} />`,
 * the same function every time for the same name — for a surface that hands an
 * icon around as a component (a choice's `icon`). Null for a name the library
 * does not have, as `Icons[name]` was undefined.
 */
export function libraryIconComponent(name) {
	if (!isLibraryIconName(name)) return null;
	let Component = COMPONENTS.get(name);
	if (!Component) {
		Component = function LibraryIconOf(props) {
			return <LibraryIcon name={name} {...props} />;
		};
		Component.displayName = `LibraryIcon(${name})`;
		COMPONENTS.set(name, Component);
	}
	return Component;
}
